import React, { useState, useEffect } from 'react';
import { rideService } from '../../../../services/rideService';
import { useAuth } from '../../../../contexts/AuthContext';
import RideCard from './RideCard';
import CreateRideModal from './CreateRideModal';
import Icon from '../../../../components/AppIcon';

const RideList = ({ eventId, isRegistered = false, isAdmin = false }) => {
  const { user } = useAuth();
  const [rides, setRides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const fetchRides = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await rideService?.getRidesByEvent(eventId);
        setRides(data || []);
      } catch (err) {
        console.error('Error fetching rides:', err);
        setError(err?.message || 'Araç paylaşımları yüklenirken hata oluştu');
      } finally {
        setIsLoading(false);
      }
    };

    if (eventId) {
      fetchRides();
    }
  }, [eventId, refreshTrigger]);

  const handleRideCreated = () => {
    setShowCreateModal(false);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleRideAction = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // 1. STRICT GATEKEEPER: If not registered and not admin, return ONLY the lock screen.
  if (!isRegistered && !isAdmin) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 mt-4">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Araç Paylaşımları Kilitli</h3>
        <p className="text-gray-500 max-w-md mx-auto mb-6">
          Araç paylaşım ilanlarını görmek ve ilan oluşturmak için önce bu etkinliğe kayıt olmalısın.
        </p>
      </div>
    );
  }

  // 2. AUTHORIZED VIEW: If code reaches here, user is allowed.
  // Show loading, error, or the full UI with "Create Ride" button AND the list.

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
          <p className="text-muted-foreground text-sm">Araç paylaşımları yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <Icon name="AlertCircle" size={32} className="text-red-500 mx-auto mb-3" />
        <p className="text-red-700 font-medium mb-2">Bir Hata Oluştu</p>
        <p className="text-red-600 text-sm">{error}</p>
        <button
          onClick={() => setRefreshTrigger(prev => prev + 1)}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-foreground">Araç Paylaşımı</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Etkinliğe birlikte gidebileceğiniz kişileri bulun
          </p>
        </div>
        {user && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium text-sm"
          >
            <Icon name="Plus" size={18} />
            İlan Oluştur
          </button>
        )}
      </div>
      {/* Empty State */}
      {rides?.length === 0 ? (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-8 text-center">
          <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-sm">
            <Icon name="Car" size={32} className="text-primary" />
          </div>
          <h4 className="text-lg font-semibold text-foreground mb-2">
            Henüz Araç Paylaşımı Yok
          </h4>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Bu etkinlik için henüz araç paylaşımı yok. İlk ilanı sen aç!
          </p>
          {user && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              <Icon name="Plus" size={20} />
              İlk İlanı Oluştur
            </button>
          )}
        </div>
      ) : (
        /* Ride Cards Grid */
        (<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rides?.map(ride => (
            <RideCard
              key={ride?.id}
              ride={ride}
              currentUserId={user?.id}
              eventId={eventId}
              onAction={handleRideAction}
            />
          ))}
        </div>)
      )}
      {/* Create Ride Modal */}
      {showCreateModal && (
        <CreateRideModal
          eventId={eventId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleRideCreated}
        />
      )}
    </div>
  );
};

export default RideList;